import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { NewsPost, formatNewsDate, getArticle } from '../utils/newsApi';
import { usePageMeta } from '../utils/usePageMeta';

const NewsArticlePage: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const [post, setPost] = useState<NewsPost | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'notFound' | 'error'>('loading');

  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    setPost(null);
    getArticle(slug, controller.signal)
      .then((article) => {
        setPost(article);
        setStatus(article ? 'ready' : 'notFound');
      })
      .catch((err) => {
        if (err.name !== 'AbortError') setStatus('error');
      });
    return () => controller.abort();
  }, [slug]);

  usePageMeta(
    post ? post.seo_title || `${post.title} | The Creamery` : 'News | The Creamery',
    post?.seo_metadesc
  );

  const html = useMemo(() => (post ? DOMPurify.sanitize(post.content) : ''), [post]);

  return (
    <div className="pt-20 bg-primary-black min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <Link to="/news" className="text-primary-gold hover:text-white transition-colors text-sm">
          &larr; All news
        </Link>

        {status === 'loading' && <p className="text-gray-400 mt-10" aria-live="polite">Loading...</p>}
        {status === 'error' && (
          <p role="alert" className="text-gray-300 mt-10">
            We couldn't load this story just now. Please try again later.
          </p>
        )}
        {status === 'notFound' && (
          <div className="mt-10">
            <h1 className="text-3xl font-bold text-primary-gold font-display mb-4">Story not found</h1>
            <p className="text-gray-300">
              We couldn't find that story. <Link to="/news" className="text-primary-gold underline">See all news</Link>
            </p>
          </div>
        )}
        {status === 'ready' && post && (
          <article className="mt-10">
            <p className="text-sm text-primary-gold mb-2">{formatNewsDate(post.created_at)}</p>
            <h1 className="text-4xl md:text-5xl font-bold text-primary-gold mb-8 font-display">{post.title}</h1>
            <div className="news-content" dangerouslySetInnerHTML={{ __html: html }} />
          </article>
        )}
      </div>
    </div>
  );
};

export default NewsArticlePage;
